import { useRef, useState } from "react";
import { api, fileUrl, fmtDate, uploadFiles } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImageIcon, Loader2, Paperclip, Send, X } from "lucide-react";
import { toast } from "sonner";

export default function TicketChat({ ticketId, messages, onNew, currentUser }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    try {
      const attachments = await uploadFiles(files);
      const { data } = await api.post(`/tickets/${ticketId}/messages`, { message: text, attachments });
      onNew(data);
      setText("");
      setFiles([]);
    } catch (e) {
      toast.error("Falha ao enviar mensagem");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="ticket-chat">
      <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1 mb-4">
        {messages.length === 0 && (
          <p className="text-xs text-zinc-500 text-center py-6">Nenhuma mensagem ainda. Inicie a conversa com o suporte.</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUser?.id;
          const fromSupport = ["tenant_admin", "agent"].includes(m.sender_role);
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
              <div className={`max-w-[80%] rounded-lg px-3.5 py-2.5 ${mine ? "bg-indigo-600 text-white" : fromSupport ? "bg-zinc-800 text-zinc-100" : "bg-zinc-900 border border-zinc-800 text-zinc-200"}`}>
                <p className={`text-[10px] font-semibold mb-1 ${mine ? "text-indigo-200" : "text-zinc-500"}`}>
                  {m.sender_name} {fromSupport && !mine && "· Suporte"}
                </p>
                {m.message && <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>}
                {m.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {m.attachments.map((p) => (
                      <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(p)} alt="anexo" className="h-16 w-16 object-cover rounded border border-black/20" />
                      </a>
                    ))}
                  </div>
                )}
                <p className={`text-[10px] mt-1.5 ${mine ? "text-indigo-200/70" : "text-zinc-600"}`}>{fmtDate(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((f, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-md px-2 py-1">
              <ImageIcon className="h-3 w-3" /> {f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-zinc-200"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <Textarea
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Escreva uma mensagem…"
          className="bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md min-h-[44px] max-h-32 resize-none focus:border-indigo-500"
        />
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden
          data-testid="chat-file-input"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files)].slice(0, 5))} />
        <Button data-testid="chat-attach-button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()}
          className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 shrink-0">
          <Paperclip className="h-4 w-4" />
        </Button>
        <Button data-testid="chat-send-button" onClick={send} disabled={sending || (!text.trim() && files.length === 0)}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md shrink-0 transition-colors duration-200">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
